<script setup lang="ts">
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import { computed, ComputedRef, onMounted, type Ref, ref } from 'vue';
import { RobotVisualizer } from '@therobotcompany/robot-visualizer';
import cloneDeep from 'lodash.clonedeep';
import gettext from '@/i18n/gettext';
import RobotModuleSelector from '@/components/atoms/RobotModuleSelector.vue';
import DraggableModuleList from '@/components/atoms/DraggableModuleList.vue';
import type { ModuleIdNameMapping, SampleRobotConfig } from '@/models/Robot';
import type { ModuleFolderMapping } from '@/models/RobotFinder';
import {
  getLatestRobotModules,
  getModuleIdNameMapping,
} from '@/helpers/RobotHelper';
import SampleRobotSelector from '@/components/atoms/SampleRobotSelector.vue';
import { configureVirtualRobot } from '@/api/VirtualRobotApi';
import { ROBOT_CONFIGURATION_TYPES, RobotFinderType } from '@/models/Robot';
import { DEFAULT_DISPLAY_ANGLES, MODULES_BASE_URL } from '@/Constants';
import RobotFinderApi from '@/api/RobotFinderApi';
import { useAlertsStore } from '@/store/AlertsStore';

const { $gettext } = gettext;

const alertsStore = useAlertsStore();
const showDialog = ref(false);
const configErrorMessage: Ref<string | null> = ref(null);
const robotType = ref(RobotFinderType.SAMPLE_CONFIG);
const selectedModules: Ref<ModuleIdNameMapping[]> = ref([]);
const latestRobotModules: Ref<ModuleFolderMapping> = ref({});
const loadingSpinnerStore = useLoadingSpinnerStore();

const moduleIdNameMapping: ComputedRef<ModuleIdNameMapping[]> = computed(() => {
  return getModuleIdNameMapping(latestRobotModules.value);
});

const selectedModuleIds = computed(() =>
  selectedModules.value.map((module) => module.id),
);
const displayPose: Ref<number[]> = ref([]);

const validationError: Ref<string | null> = ref(null);

const canApplyConfig = computed(
  () => selectedModules.value.length > 0 && !validationError.value,
);

async function calculateDisplayPose() {
  const displayPoseResponse = await RobotFinderApi.getDisplayPose(
    selectedModuleIds.value.join(','),
  );

  if (displayPoseResponse) {
    displayPose.value = displayPoseResponse;
  } else {
    displayPose.value = DEFAULT_DISPLAY_ANGLES;
  }
}

async function addModule(selectedModule: ModuleIdNameMapping) {
  selectedModules.value.push(selectedModule);
  await calculateDisplayPose();
}

async function addModulesBulk(modules: ModuleIdNameMapping[]) {
  selectedModules.value.push(...modules);
  await calculateDisplayPose();
}

function onUnmatchedIds(ids: string[]) {
  alertsStore.error(
    $gettext('Module IDs not found: %{ids}', { ids: ids.join(', ') }),
  );
}

async function removeModule(index: number) {
  selectedModules.value.splice(index, 1);
  await calculateDisplayPose();
  robotType.value = RobotFinderType.CUSTOM_CONFIG;
}

async function updateModules(newModules: ModuleIdNameMapping[]) {
  selectedModules.value = newModules;
  await calculateDisplayPose();
  robotType.value = RobotFinderType.CUSTOM_CONFIG;
}

async function onValidationError(error: string | null) {
  validationError.value = error;
}

async function clearAllModules() {
  selectedModules.value = [];
  await calculateDisplayPose();
  robotType.value = RobotFinderType.CUSTOM_CONFIG;
}

async function onSampleConfigSelected(
  sampleConfig: SampleRobotConfig | undefined,
) {
  if (sampleConfig && moduleIdNameMapping.value) {
    selectedModules.value = [];
    const newSelection = [];
    for (let i = 0; i < sampleConfig?.moduleIds.length; i++) {
      const newMapping = moduleIdNameMapping.value.find(
        (mapping: ModuleIdNameMapping) =>
          parseInt(mapping.id, 10) === parseInt(sampleConfig?.moduleIds[i], 10),
      );
      if (newMapping) {
        newSelection.push(newMapping);
      }
    }
    selectedModules.value = cloneDeep(newSelection);
    await calculateDisplayPose();
  }
}
function onRobotTypeChanged() {
  if (robotType.value === RobotFinderType.SAMPLE_CONFIG) {
    selectedModules.value = [];
  }
}

function setInput(index: number, value: boolean) {
  configureVirtualRobot({
    type: 'io',
    bank_index: 0,
    output_index: index, // TODO: why is this not input_index?
    value,
  });
}

function setSafety(value: boolean) {
  configureVirtualRobot({
    type: 'safety',
    active: value,
  });
}

async function setRobotModuleConfig() {
  configErrorMessage.value = null;
  showDialog.value = false;
  loadingSpinnerStore.setShowLoadingOverlay(
    true,
    true,
    $gettext('Configuring Virtual Robot...'),
    'background',
  );
  loadingSpinnerStore.setLoadingOverlayProgressPercent(50);

  try {
    await configureVirtualRobot({
      type: 'module_ids',
      module_ids: selectedModules.value.map((module) =>
        parseInt(module.id, 10),
      ),
    });
    loadingSpinnerStore.setLoadingOverlayProgressPercent(100);
    setTimeout(() => {
      loadingSpinnerStore.setShowLoadingOverlay(false);
    }, 1500);
  } catch (error: unknown) {
    loadingSpinnerStore.setShowLoadingOverlay(false);
    showDialog.value = true; // Reopen config dialog to show error
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err: any = error;
    const backendMessage = err?.response?.data?.message || err?.message;
    configErrorMessage.value = backendMessage
      ? `${$gettext('Failed to apply configuration')}: ${backendMessage}`
      : $gettext('Failed to apply configuration');
  }
}

onMounted(async () => {
  const moduleFolderMapping = await getLatestRobotModules();

  if (moduleFolderMapping) {
    latestRobotModules.value = moduleFolderMapping;
  } else {
    console.error("Couldn't load modules");
  }
});
</script>
<template>
  <v-dialog
    max-width="960px"
    style="
      width: 100%;
      margin-left: 32px;
      margin-top: 88px;
      margin-bottom: 24px;
    "
    :model-value="showDialog"
    persistent
  >
    <v-card style="overflow: hidden">
      <v-card-title>{{ $gettext('Virtual Robot Configuration') }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="5">
            <h4>{{ $gettext('Robot Module Configuration') }}</h4>
          </v-col>
          <v-col v-if="selectedModules.length > 0" cols="5" sm="3">
            <h4 style="padding-left: 12px">
              {{ $gettext('Selected Modules') }}
              <v-icon
                variant="outlined"
                color="error"
                size="small"
                class="cursor-pointer"
                icon="mdi-close"
                @click="clearAllModules"
              />
            </h4>
          </v-col>
        </v-row>
        <v-row style="min-height: 150px">
          <v-col cols="12" sm="5">
            <v-select
              v-model="robotType"
              :label="$gettext('Robot type')"
              :items="ROBOT_CONFIGURATION_TYPES"
              @update:modelValue="onRobotTypeChanged"
            />
            <robot-module-selector
              v-if="robotType === RobotFinderType.CUSTOM_CONFIG"
              :module-folder-mapping="latestRobotModules"
              @onModuleSelected="addModule"
              @onModulesBulkSelected="addModulesBulk"
              @onUnmatchedIds="onUnmatchedIds"
            />
            <sample-robot-selector
              v-else
              @onSampleConfigSelected="onSampleConfigSelected"
            />
            <div v-if="validationError" class="text-error mt-3">
              {{ validationError }}
            </div>
          </v-col>
          <template v-if="selectedModules.length > 0">
            <v-col cols="5" sm="3">
              <div style="height: 150px; overflow-y: auto; overflow-x: hidden">
                <draggable-module-list
                  :modules="selectedModules"
                  :module-folder-mapping="latestRobotModules"
                  @update:modules="updateModules"
                  @remove="removeModule"
                  @clear="clearAllModules"
                  @validation-error="onValidationError"
                />
              </div>
            </v-col>
            <v-col cols="7" sm="4">
              <div
                class="mb-2"
                style="
                  height: 150px;
                  border-radius: 16px;
                  background-color: white;
                  outline: 8px solid white;
                "
              >
                <robot-visualizer
                  v-if="selectedModules.length > 0"
                  :modules-base-url="MODULES_BASE_URL"
                  background-color="#fff"
                  :current-angles="displayPose"
                  :module-ids="selectedModuleIds"
                  hide-axes-helper
                />
              </div>
            </v-col>
          </template>
          <v-col v-else>
            <div
              class="text-center font-weight-bold d-flex justify-center align-center w-100"
              style="
                margin-left: auto;
                margin-right: auto;
                border-radius: 16px;
                border: 2px dashed;
                height: 135px;
              "
            >
              {{ $gettext('Please select a Robot first') }}
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h4>{{ $gettext('Inputs') }}</h4>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="index in Array(8).keys()"
            v-bind:key="index"
            cols="12"
            sm="6"
          >
            <span class="d-inline-block" style="width: 56px">
              {{ $gettext('Input') }} {{ index + 1 }}
            </span>
            <v-btn
              color="success"
              class="ml-3"
              style="margin-top: -4px"
              @click="setInput(index, true)"
              >{{ $gettext('enable') }}</v-btn
            >
            <v-btn
              color="error"
              class="ml-3"
              style="margin-top: -4px"
              @click="setInput(index, false)"
            >
              {{ $gettext('disable') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="mt-8">
          <v-col>
            <h4>{{ $gettext('Safety control') }}</h4>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <span>{{ $gettext('Safety control') }}</span>
            <v-btn
              color="success"
              class="ml-3"
              style="margin-top: -4px"
              @click="setSafety(true)"
            >
              {{ $gettext('enable') }}
            </v-btn>
            <v-btn
              color="error"
              class="ml-3"
              style="margin-top: -4px"
              @click="setSafety(false)"
            >
              {{ $gettext('disable') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-if="configErrorMessage" class="mt-8">
          <v-col>
            <v-alert type="error" density="comfortable" variant="tonal">
              {{ configErrorMessage }}
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn @click="showDialog = false">{{ $gettext('Close') }}</v-btn>
        <v-spacer />
        <r-btn
          v-if="selectedModules.length > 0"
          variant="accent"
          :disabled="!canApplyConfig"
          @click="setRobotModuleConfig"
        >
          {{ $gettext('Apply module configuration') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <div class="position-relative">
    <r-btn
      variant="outlined"
      class="open-robot-config-button"
      @click="showDialog = true"
      >{{ $gettext('Configure Virtual Robot') }}</r-btn
    >
  </div>
</template>

<style lang="scss">
.open-robot-config-button {
  position: absolute;
  bottom: 16px;
  left: 16px;
}

.v-dialog {
  .v-overlay__content {
    max-height: 100% !important;
  }
}
</style>
